"use client";

import { useState } from "react";
import { initialen } from "./formular/auswahl-konfiguration";
import { PersonenKarte, type PersonenKartenDaten } from "./personen-karte";

/**
 * Aktivitäts-Feed (Musterkatalog M-011 AD6, Design-Guide `aktivitaets-feed.md`): Kommentare und Verlaufssätze in EINEM
 * Strang, neueste zuletzt (wie im Gespräch), mit Filter „Nur Kommentare“. Ein Kommentar trägt Avatar (Initialen), Name,
 * Zeit und Text; ein Verlaufssatz ist eine leise Zeile mit Punkt. Der Baustein bekommt fertige Zeilen (Texte, Zeiten,
 * Erwähnungen als Elemente) und kennt weder Quellen noch Rechte: der Aufrufer liefert nur, was die Person lesen darf.
 * Abweichung AW-040 gegenüber der Vorschau b05: „Mehr anzeigen (n ältere)“ steht OBEN im Strang (die älteren Einträge liegen davor, neueste
 * zuletzt), nicht darunter; Datumsfeld bleibt das native Feld.
 */
export interface FeedEintrag {
  id: string;
  art: "kommentar" | "verlauf";
  /** Name der Person (für Kommentare Pflicht; bei Verlaufssätzen steht er im Satz). */
  autor: string | null;
  /** Angaben für die Personen-Karte am Autorennamen; fehlt/`null` = Name ohne Karte (System, Unbekannt, E-Mail-artiger Name). */
  autorKarte?: PersonenKartenDaten | null;
  /** Fertig formatierte Zeit („heute 14:12“, „03.10.2026 09:48“). */
  zeit: string;
  /** Kommentartext (mit Erwähnungen) bzw. der Verlaufssatz ohne Namen. */
  inhalt: React.ReactNode;
  /** Kommentar wurde nach dem Senden geändert. */
  geaendert?: boolean;
}

const FILTER_AKTIV = "py-1 px-3 text-xs font-medium rounded-full bg-layer shadow-sm text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
const FILTER_INAKTIV = "py-1 px-3 text-xs font-medium rounded-full text-muted-foreground-1 hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";

/** Zahl der Einträge, die der Feed zeigt, und der Schritt von „Mehr anzeigen“ (PM Paket 3b, B5). */
export const FEED_SEITE = 15;

export function AktivitaetsFeed({
  eintraege,
  nurKommentare,
  onFilter,
  anzahlKommentare,
  leerText = "Noch keine Aktivität.",
  leerKommentareText = "Noch keine Kommentare.",
  seite = FEED_SEITE,
}: {
  eintraege: FeedEintrag[];
  nurKommentare: boolean;
  onFilter: (nurKommentare: boolean) => void;
  anzahlKommentare: number;
  leerText?: string;
  leerKommentareText?: string;
  /** Anzahl der zuerst gezeigten (neuesten) Einträge und Schrittweite von „Mehr anzeigen“. Gekürzt wird nur die Anzeige. */
  seite?: number;
}) {
  const gefiltert = nurKommentare ? eintraege.filter((e) => e.art === "kommentar") : eintraege;
  // Reihenfolge: älteste zuerst, neueste unten. Gezeigt werden die neuesten `zeige`; die älteren stehen hinter dem Schritt oben im Strang.
  const [zeige, setZeige] = useState(seite);
  const ersterIndex = Math.max(0, gefiltert.length - zeige);
  const sichtbar = gefiltert.slice(ersterIndex);
  const aelter = ersterIndex;
  const waehleFilter = (nur: boolean) => {
    setZeige(seite);
    onFilter(nur);
  };
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-activity-feeds/collapsible-activity-feed */}
      <div>
        <div className="mb-4 inline-flex gap-1 p-1 bg-surface rounded-full" role="group" aria-label="Aktivität filtern">
          <button type="button" className={nurKommentare ? FILTER_INAKTIV : FILTER_AKTIV} aria-pressed={!nurKommentare} onClick={() => waehleFilter(false)}>
            Alles ({eintraege.length})
          </button>
          <button type="button" className={nurKommentare ? FILTER_AKTIV : FILTER_INAKTIV} aria-pressed={nurKommentare} onClick={() => waehleFilter(true)}>
            Nur Kommentare ({anzahlKommentare})
          </button>
        </div>
        {aelter > 0 && (
          <p className="mb-3 text-xs text-muted-foreground-1" role="status">
            {sichtbar.length} von {gefiltert.length} Einträgen
          </p>
        )}
        {sichtbar.length === 0 ? (
          <p className="text-sm text-muted-foreground-1">{nurKommentare ? leerKommentareText : leerText}</p>
        ) : (
          <ol className="relative">
            {aelter > 0 && (
              <li className="relative pb-8 after:absolute after:top-9.5 after:bottom-0 after:inset-s-[19px] after:border-s after:border-line-2">
                <button
                  type="button"
                  className="group w-full text-start flex items-center gap-x-5 focus:outline-hidden"
                  aria-label={`Mehr anzeigen, ${aelter} ältere Einträge`}
                  onClick={() => setZeige((z) => z + seite)}
                >
                  <span className="w-9.5" aria-hidden="true">
                    <span className="flex shrink-0 justify-center items-center w-6 h-7 bg-layer border border-layer-line text-sm font-semibold text-layer-foreground rounded-md mx-auto">
                      {/* Lucide „ChevronsUpDown“ als Inline-SVG (das Paket bindet lucide-react nicht ein) */}
                      <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m7 15 5 5 5-5" />
                        <path d="m7 9 5-5 5 5" />
                      </svg>
                    </span>
                  </span>
                  <span className="grow text-sm font-semibold text-foreground group-hover:text-primary-hover group-focus-visible:text-primary-focus">
                    Mehr anzeigen ({aelter} ältere)
                  </span>
                </button>
              </li>
            )}
            {sichtbar.map((e) =>
              e.art === "kommentar" ? (
                <li key={e.id} className="relative last:pb-0 pb-6 last:after:hidden after:absolute after:top-9.5 after:bottom-0 after:inset-s-[19px] after:border-s after:border-line-2">
                  <div className="w-full flex gap-x-4">
                    <span className="flex shrink-0 justify-center items-center size-9.5 bg-layer border border-layer-line text-sm font-semibold uppercase text-layer-foreground rounded-full" aria-hidden="true">
                      {initialen(e.autor ?? "?")}
                    </span>
                    <div className="grow min-w-0">
                      <p className="text-sm font-semibold text-foreground">
                        {e.autorKarte ? <PersonenKarte person={e.autorKarte}>{e.autor}</PersonenKarte> : (e.autor ?? "Unbekannt")}
                        <span className="ms-2 text-xs font-normal text-muted-foreground-1">{e.zeit}</span>
                        {e.geaendert && <span className="ms-2 text-xs font-normal text-muted-foreground-1">bearbeitet</span>}
                      </p>
                      <p className="mt-0.5 text-sm text-foreground whitespace-pre-line break-words">{e.inhalt}</p>
                    </div>
                  </div>
                </li>
              ) : (
                <li key={e.id} className="relative last:pb-0 pb-4 last:after:hidden after:absolute after:top-4 after:bottom-0 after:inset-s-[19px] after:border-s after:border-line-2">
                  <div className="w-full flex items-start gap-x-4">
                    <span className="flex shrink-0 justify-center items-center size-9.5" aria-hidden="true">
                      <span className="size-2 rounded-full bg-line-3" />
                    </span>
                    <p className="grow min-w-0 pt-1.5 text-xs text-muted-foreground-1">
                      {e.autor && (
                        <span className="font-medium text-foreground">
                          {e.autorKarte ? <PersonenKarte person={e.autorKarte} groesse="xs">{e.autor}</PersonenKarte> : e.autor}{" "}
                        </span>
                      )}
                      {e.inhalt}
                      <span className="ms-2">{e.zeit}</span>
                    </p>
                  </div>
                </li>
              ),
            )}
          </ol>
        )}
      </div>
    </>
  );
}
